import { Component, TemplateFunction } from '@ribajs/core'
import { SbSidebarComponent } from './sb-sidebar.component.js'
import { SbPopupComponent } from './sb-popup.component.js'

import type { SidebarState, PopupState } from '../types/index.js'
import type { JsxSbMaskProps } from '../types/jsx'

/**
 * A custom mask element, can be used to display a mask under the sidebar or any other element like dialogs
 */
export class SbMaskComponent extends Component {
  public static tagName = 'sb-mask'

  protected sidebars: SbSidebarComponent[] = []
  protected popups: SbPopupComponent[] = []

  /** The body element */
  protected body = window.document.body as HTMLBodyElement

  static get observedAttributes(): (keyof JsxSbMaskProps)[] {
    return ['visible']
  }

  protected requiredAttributes(): string[] {
    return []
  }

  public scope = {
    // Options
    visible: false,
    // Methods
    hideAll: this.hideAll,
  }

  /** Close / hide all open / visible elements (e.g. sidebars) */
  public hideAll() {
    for (const sidebar of this.sidebars) {
      if (sidebar.getState().includes('overlap')) {
        sidebar.hide()
      }
    }
    for (const popup of this.popups) {
      if (popup.getState().visible) {
        popup.hide()
      }
    }
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbMaskComponent.observedAttributes)
  }

  /** Check if no element (e.g. sidebar) is open */
  protected noOneOpen() {
    const noSidebarOpen = this.sidebars.every(
      sidebar => !sidebar.getState().includes('overlap'),
    )
    const noPopupOpen = this.popups.every(popup => !popup.getState().visible)
    return noSidebarOpen && noPopupOpen
  }

  /** Check if any element (e.g. sidebar) is open */
  protected anyIsOpen() {
    const anySidebarOpen = this.sidebars.some(sidebar =>
      sidebar.getState().includes('overlap'),
    )
    const anyPopupOpen = this.popups.some(popup => popup.getState().visible)
    return anySidebarOpen || anyPopupOpen
  }

  protected onElementHidden() {
    this.scope.visible = !this.noOneOpen()

    // Disable body scroll
    if (!this.scope.visible) {
      this.body.classList.remove('overflow-hidden')
    }
  }

  protected onElementVisible() {
    this.scope.visible = this.anyIsOpen()

    // Disable body scroll
    if (this.scope.visible) {
      this.body.classList.add('overflow-hidden')
    }
  }

  /** Handle sidebar state change */
  protected _onSidebarStateChange(event: CustomEventInit<SidebarState>) {
    switch (event.detail) {
      case 'side-left':
      case 'side-right':
        // Do nothing yet
        break
      case 'overlap-left':
      case 'overlap-right':
        this.onElementVisible()
        break
      case 'move-left':
      case 'move-right':
        // Do nothing yet
        break
      default:
        this.onElementHidden()
        break
    }
  }
  /** Handle sidebar state change */
  onSidebarStateChange = this._onSidebarStateChange.bind(this)

  /** Handle sidebar state change */
  protected _onPopupStateChange(event: CustomEventInit<PopupState>) {
    if (event.detail?.visible) {
      this.onElementVisible()
    } else {
      this.onElementHidden()
    }
  }
  /** Handle sidebar state change */
  onPopupStateChange = this._onPopupStateChange.bind(this)

  protected async afterBind() {
    await super.afterBind()
    this.initSidebars()
    this.initPopups()
    this.classList.add(`${SbMaskComponent.tagName}-ready`)
  }

  protected initSidebars() {
    this.sidebars =
      Array.from(document.querySelectorAll(SbSidebarComponent.tagName)) ||
      undefined
    for (const sidebar of this.sidebars) {
      sidebar.removeEventListener('state-changed', this.onSidebarStateChange)
      sidebar.addEventListener('state-changed', this.onSidebarStateChange)
    }
  }

  protected initPopups() {
    this.popups =
      Array.from(document.querySelectorAll(SbPopupComponent.tagName)) ||
      undefined
    for (const popup of this.popups) {
      popup.removeEventListener('state-changed', this.onPopupStateChange)
      popup.addEventListener('state-changed', this.onPopupStateChange)
    }
  }

  // deconstruction
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected removeEventListeners() {
    for (const sidebar of this.sidebars) {
      sidebar.removeEventListener('state-changed', this.onSidebarStateChange)
    }
    for (const popup of this.popups) {
      popup.removeEventListener('state-changed', this.onPopupStateChange)
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return (
      <div class="mask" rv-class-show="visible" rv-on-click="hideAll"></div>
    )
  }
}
